import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import type { ColumnDef } from "@tanstack/react-table";
import { adminApi, TAG_TYPE_LABEL, type TagOrderRow } from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { queryTableProps } from "@/lib/data-table";
import { tagOrderTone } from "@/lib/status-tone";

export const Route = createFileRoute("/_authed/tag-orders")({
  component: TagOrdersPage,
});

const STATUS_FILTERS = ["", "pending", "paid", "refunded", "abandoned", "payment_init_failed"];
const PAGE_SIZE = 50;


function TagOrdersPage() {
  const [status, setStatus] = useState<string>("");

  const ordersQuery = useInfiniteQuery({
    queryKey: queryKeys.tagOrders.list({ status }),
    queryFn: async ({ pageParam }) => {
      const res = await adminApi.listTagOrders()({
        ...(status ? { status } : {}),
        limit: PAGE_SIZE,
        cursor: pageParam,
      });
      return res.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: LIST_STALE_MS,
  });

  const rows = ordersQuery.data?.pages.flatMap((p) => p.orders) ?? [];

  const columns = useMemo<ColumnDef<TagOrderRow, unknown>[]>(
    () => [
      {
        header: "Order ID",
        accessorKey: "id",
        cell: ({ row }) => <span className="font-mono text-[10px]">{row.original.id.slice(0, 12)}…</span>,
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <StatusPill tone={tagOrderTone(row.original.status)}>{row.original.status}</StatusPill>
        ),
      },
      { header: "Package", accessorKey: "packageName" },
      {
        header: "Qty",
        accessorKey: "quantity",
        cell: ({ row }) => row.original.quantity ?? "—",
      },
      {
        header: "Amount",
        accessorKey: "amountPence",
        cell: ({ row }) =>
          row.original.amountPence != null
            ? `${row.original.currency.toUpperCase()} ${(row.original.amountPence / 100).toFixed(2)}`
            : "—",
      },
      {
        header: "Credits",
        cell: ({ row }) => {
          const { creditLines, creditsGranted, creditsRevoked } = row.original;
          if (creditsGranted === 0) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <span className="font-mono text-xs">
              {creditLines.map((l) => `${l.count} ${TAG_TYPE_LABEL[l.type] ?? l.type}`).join(" · ")}
              {creditsRevoked > 0 ? (
                <span className="ml-1 text-destructive">(−{creditsRevoked} refunded)</span>
              ) : null}
            </span>
          );
        },
      },
      {
        header: "Created",
        cell: ({ row }) =>
          row.original.createdAtMillis
            ? format(new Date(row.original.createdAtMillis), "yyyy-MM-dd HH:mm")
            : "—",
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Tag orders</h1>
        <p className="text-sm text-muted-foreground">
          Stripe checkout orders. A paid order grants typed tag credits to the buyer; a full
          refund takes back whatever is still unspent.
        </p>
      </div>

      <Card>
        <CardContent className="flex items-end gap-3 p-4">
          <div>
            <label className="text-xs text-muted-foreground">Status</label>
            <select
              className="block rounded border bg-background px-2 py-1 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s || "any"} value={s}>
                  {s || "any"}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <DataTable
        data={rows}
        columns={columns}
        rowKey={(row) => row.id}
        emptyMessage="No orders match this filter."
        {...queryTableProps(ordersQuery)}
        hasMore={!!ordersQuery.hasNextPage}
        isFetchingMore={ordersQuery.isFetchingNextPage}
        onLoadMore={() => void ordersQuery.fetchNextPage()}
      />
    </div>
  );
}
